import { get, has, set } from 'lodash';
import React, { FC, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';

import { Body, Row, Section } from 'src/presentation/components/forms/Layout';
import { Button } from 'src/presentation/components/agent_desktop_components';
import Footer from 'src/presentation/components/forms/Footer';
import Form, { FormEditorProps, useForm } from 'src/presentation/components/forms/Form';
import { getErrorMessage, validateSSOConfig } from 'src/actions/integrations/sso/editorHelpers';
import InputField from 'src/presentation/components/forms/InputField';
import organizationResource from 'src/actions/organization/organizationResource';
import RadioButton from '@ad-scripts/presentation/components/common/radio_button';
import ssoConfigResource, { MetadataChoice } from 'src/actions/integrations/sso/ssoConfigResource';
import Switch from 'src/presentation/components/forms/Switch';
import Textarea from 'src/presentation/components/forms/Textarea';
import useDocumentResource, { useResourceValue } from 'src/presentation/hooks/useDocumentResource';
import { useOnSave } from './helpers/useOnSave';
import { MappingSection } from './components/MappingSection';
import useIsFeatureEnabled from 'src/presentation/hooks/useIsFeatureEnabled';

const SsoForm: FC<FormEditorProps> = ({ item, errors, root }) => {
  const resetErrors = ssoConfigResource.actions.resetErrors as any;
  const update = ssoConfigResource.effects.replace;

  const formContext = useForm({
    item,
    errors,
    resetErrors,
    root,
    update,
  });

  const { overrideState, updateState, workingState } = formContext;

  const isJitUserCreationEnabled = useIsFeatureEnabled()('allowJitUserCreation');

  const { saveHandler: onSave } = useOnSave({
    ...formContext,
    resetErrors,
    update,
    validate: validateSSOConfig,
    workingState,
  });

  const dispatch = useDispatch();

  const { value } = useDocumentResource(ssoConfigResource, { fetch: false });
  useEffect(() => overrideState(value), [overrideState, value]);

  const updateMetadataSelection = useCallback(
    selection => {
      updateState(set({}, 'metadataOption', selection));
      errors.length && dispatch(resetErrors());
    },
    [dispatch, errors.length, resetErrors, updateState]
  );

  const onEnabledChange = useCallback(
    value => {
      updateState(set({}, 'enabled', value));
      errors.length && dispatch(resetErrors());
    },
    [dispatch, errors.length, resetErrors, updateState]
  );

  const onMetadataChange = useCallback(
    ev => {
      updateState(set({}, 'metadata', ev.target.value));
      errors.length && dispatch(resetErrors());
    },
    [dispatch, errors.length, resetErrors, updateState]
  );

  const onMetadataURLChange = useCallback(
    url => {
      updateState(set({}, 'metadataURL', url));
      errors.length && dispatch(resetErrors());
    },
    [dispatch, errors.length, resetErrors, updateState]
  );

  const filteredErrors = errors.filter(
    err => err.attr !== 'metadata' && err.attr !== 'metadataURL' && !err.attr.startsWith('idpAttributeMappingsConfig.')
  );

  const { value: organization } = useResourceValue(organizationResource);

  const showMappingSection = workingState.enabled && workingState.jitUserCreation && isJitUserCreationEnabled;

  return (
    <Form context={formContext}>
      <FormBody data-aid="admin-sso-editor">
        <Section>
          <EnableConfigFormRow>
            <div>
              <h3>Use SSO</h3>
              <SSOConfigDescription>Use your SSO identity provider for user login</SSOConfigDescription>
            </div>
            <EnableConfigToggleContainer>
              <Switch
                data-aid={workingState.enabled ? 'admin-sso-configuration-enabled' : 'admin-sso-configuration-disabled'}
                attr="enabled"
                label=""
                onChange={onEnabledChange}
              />
            </EnableConfigToggleContainer>
          </EnableConfigFormRow>
          {workingState.enabled ? renderAdditionalFields(isJitUserCreationEnabled) : null}
          <StyledErrorSection>
            {filteredErrors.map(err => (
              <div key={err.code}>{getErrorMessage(err)}</div>
            ))}
          </StyledErrorSection>
          {renderGladlyURLs()}
        </Section>

        {showMappingSection ? <MappingSection /> : null}
      </FormBody>

      <Footer>
        <Button data-aid="admin-sso-editor-save" onClick={onSave} disabled={!hasChanged()}>
          Save
        </Button>
      </Footer>
    </Form>
  );

  function hasChanged() {
    if (!has(workingState, 'enabled')) {
      return false;
    } else {
      return workingState.enabled || workingState.enabled !== item.enabled;
    }
  }

  function renderAdditionalFields(jitUserCreation = false) {
    return (
      <>
        <FormRow>
          <div>
            <h3>Force users to reauthenticate</h3>
            <SSOConfigDescription>
              If users have logged out of Gladly but still have an active SSO session, they can be reauthenticated
              automatically. Alternatively, you may choose to force users to reauthenticate each time they log in.
            </SSOConfigDescription>
          </div>
          <ToggleContainer>
            <Switch
              data-aid={
                workingState.forceAuthentication
                  ? 'admin-sso-forceAuthentication-enabled'
                  : 'admin-sso-forceAuthentication-disabled'
              }
              attr="forceAuthentication"
              label=""
            />
          </ToggleContainer>
        </FormRow>
        {jitUserCreation ? (
          <FormRow>
            <div>
              <h3>Create new user on first login</h3>
              <SSOConfigDescription>
                If enabled, a new user will be created in Gladly when they log in for the first time using SSO.
              </SSOConfigDescription>
            </div>
            <ToggleContainer>
              <Switch
                data-aid={
                  workingState.jitUserCreation
                    ? 'admin-sso-jitUserCreation-enabled'
                    : 'admin-sso-jitUserCreation-disabled'
                }
                attr="jitUserCreation"
                label=""
              />
            </ToggleContainer>
          </FormRow>
        ) : null}

        <FormRow>
          <h3>Choose option for setting identity provider details</h3>
        </FormRow>
        <FormRow>
          <RadioButton
            dataAid="admin-sso-metadataUrl-selection-button"
            isChecked={workingState.metadataOption === MetadataChoice.URL}
            key="admin-sso-metadataUrl-selection-button"
            label="Fetch metadata from URL (upon Save)"
            onChange={() => updateMetadataSelection(MetadataChoice.URL)}
          />
        </FormRow>
        <FormRow>
          <InputField
            data-aid="metadataUrl"
            attr="metadataURL"
            placeholder="Enter URL for fetching metadata"
            disabled={workingState.metadataOption !== MetadataChoice.URL}
            onChange={onMetadataURLChange}
          />
        </FormRow>
        <MetadataFormRow>
          <RadioButton
            dataAid="admin-sso-metadata-selection-button"
            isChecked={workingState.metadataOption === MetadataChoice.DATA}
            key="admin-sso-metadata-selection-button"
            label="Paste metadata"
            onChange={() => updateMetadataSelection(MetadataChoice.DATA)}
          />
        </MetadataFormRow>
        <FormRow>
          <Textarea
            data-aid="metadata"
            attr="metadata"
            disabled={workingState.metadataOption !== MetadataChoice.DATA}
            placeholder="Enter metadata for your identity provider"
            onChange={onMetadataChange}
          />
        </FormRow>
      </>
    );
  }

  function renderGladlyURLs() {
    if (!workingState.enabled) {
      return null;
    }

    return (
      <EnableConfigFormRow>
        <div>
          <h3>Reference: Gladly Metadata and ACS URLs</h3>
          <SSOGladlyMetadataContainer>
            <SSOGladlyMetadataDescription>
              Some configurations may require a URL from Gladly for additional authentication. The URLs below are
              provided for your convenience.
            </SSOGladlyMetadataDescription>
            <div>
              Gladly Metadata URL:{' '}
              <span data-aid="gladly-metadata-url">{get(organization, 'gladlyUrl')}/sso/saml/metadata</span>
            </div>
            <div>
              Gladly ACS URL: <span data-aid="gladly-metadata-url">{get(organization, 'gladlyUrl')}/sso/saml/acs</span>
            </div>
          </SSOGladlyMetadataContainer>
        </div>
      </EnableConfigFormRow>
    );
  }
};

const FormBody = styled(Body)`
  max-width: 600px;
  padding-bottom: 0;
  width: 80%;
`;

const FormRow = styled(Row)`
  margin-bottom: ${p => p.theme.spacing.stackXSmall};
`;

const EnableConfigFormRow = styled(FormRow)`
  min-width: 350px;
`;

const MetadataFormRow = styled(FormRow)`
  top: ${p => p.theme.spacing.stackXSmall};
`;

const SSOConfigDescription = styled.span`
  color: ${p => p.theme.colors.gray700};
`;

const SSOGladlyMetadataContainer = styled.div`
  color: ${p => p.theme.colors.gray700};
`;

const SSOGladlyMetadataDescription = styled.div`
  padding-bottom: ${p => p.theme.spacing.stackSmall};
`;

const ToggleContainer = styled.div`
  padding: ${p => p.theme.spacing.stackSmall} 19px ${p => p.theme.spacing.stackSmall} 140px;
`;

const EnableConfigToggleContainer = styled(ToggleContainer)`
  padding-left: 195px;
  padding-right: 0;
`;

const StyledErrorSection = styled.div`
  margin-top: ${p => p.theme.spacing.stackSmall};
  color: ${p => p.theme.colors.red400};
`;

export default SsoForm;
